import React, { createContext, useContext, useEffect, useMemo, useState, useReducer } from "react";

import PriceChangesGateway from "../../../../gateways/PriceChangesGateway";
import PriceChangesMapper from "../../../../mappers/PriceChangesMapper";
import { PriceChangeInterface } from "../../../../domain/PriceChangeInterface";
import { reducer } from "./reducer";

interface PriceChangesContextValue {
  resources: PriceChangeInterface[];
  isLoading: boolean;
  totalResources: number;
  currentPage: number;
  fetch: (pageNumber?: number) => Promise<void>;
  update: (uuid: string, notify: boolean) => Promise<void>;
  updateMany: (uuids: string[], notify: boolean) => Promise<void[]>;
  ignore: (uuid: string) => Promise<void>;
  ignoreMany: (uuids: string[]) => Promise<void[]>;
  toggle: (uuid: string) => void;
  toggleAll: () => void;
}

const Context = createContext<PriceChangesContextValue | undefined>(undefined);

export function Provider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const gateway = useMemo(() => new PriceChangesGateway(new PriceChangesMapper()), []);

  const [resources, dispatch] = useReducer(reducer, []);
  const [isLoading, setIsLoading] = useState(true);
  const [totalResources, setTotalResources] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    fetch(0);
  }, [gateway]);

  const fetch = async (pageNumber = 0): Promise<void> => {
    setIsLoading(true);

    const { data, total } = await gateway.fetch(pageNumber);

    dispatch({
      type: "set",
      resources: data,
    });

    setTotalResources(total);
    setCurrentPage(pageNumber);
    setIsLoading(false);
  };

  const update = async (
    uuid: string,
    notify: boolean,
    groupUuid?: string,
    totalExpected?: number
  ): Promise<void> => {
    await gateway.apply(uuid, notify, groupUuid, totalExpected);

    dispatch({
      type: "update",
      uuid,
    });
  };

  const updateMany = async (uuids: string[], notify: boolean): Promise<void[]> => {
    const groupUuid = crypto.randomUUID();
    const totalExpected = uuids.length;

    return Promise.all(uuids.map(uuid => update(uuid, notify, groupUuid, totalExpected)));
  };

  const ignore = async (uuid: string): Promise<void> => {
    await gateway.dismiss(uuid);

    dispatch({
      type: "remove",
      uuid,
    });
  };

  const ignoreMany = async (uuids: string[]): Promise<void[]> => {
    return Promise.all(uuids.map(uuid => ignore(uuid)));
  };

  const toggle = (uuid: string) => {
    dispatch({
      type: "toggle",
      uuid,
    });
  };

  const toggleAll = () => {
    dispatch({
      type: "toggleAll",
    });
  };

  return (
    <Context.Provider
      value={{
        resources,
        isLoading,
        totalResources,
        currentPage,
        fetch,
        update,
        updateMany,
        ignore,
        ignoreMany,
        toggle,
        toggleAll,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function usePriceChanges(): PriceChangesContextValue {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error("usePriceChanges must be used within the Price Changes Provider.");
  }

  return context;
}
